import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { API, COLLECTIONS, formatPrice } from "../lib/constants";
import { Monogram } from "../components/icons";

const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original?._retry && !String(original?.url || "").includes("/auth/")) {
      original._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch (e) {}
    }
    return Promise.reject(error);
  }
);

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return "Une erreur est survenue. Réessayez.";
};

const emptyForm = {
  name: "",
  collections: ["signatures-feminines"],
  short_desc: "",
  description: "",
  conseils: "",
  notes: { tete: "", coeur: "", fond: "" },
  price: "",
  size: "30 ml",
  image_url: "",
  featured: false,
  visible: true,
  position: 0,
};

const inputCls =
  "mt-1.5 w-full rounded-lg border border-espresso/20 bg-cream px-3.5 py-2.5 text-sm text-espresso outline-none transition-colors focus:border-champagne";
const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-espresso/60";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { email, password });
      onLogin(data.user);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-espresso px-5 py-10">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-ivory p-8 sm:p-10">
        <div className="flex flex-col items-center text-center">
          <Monogram className="h-12 w-12" />
          <h1 className="mt-4 font-serif text-3xl text-espresso">Espace consultante</h1>
          <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-taupe">Vāsam — Administration</p>
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        )}
        <label className={`${labelCls} mt-7`}>
          Email
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="vous@exemple.fr"
          />
        </label>
        <label className={`${labelCls} mt-4`}>
          Mot de passe
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            placeholder="••••••••"
          />
        </label>
        <button
          data-testid="admin-login-button"
          disabled={busy}
          className="btn-pill mt-7 w-full bg-espresso py-4 text-ivory hover:bg-noir disabled:opacity-60"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Se connecter"}
        </button>
        <Link to="/" className="mt-6 block text-center text-[10px] uppercase tracking-[0.25em] text-taupe hover:text-espresso">
          Retour à la boutique
        </Link>
      </form>
    </div>
  );
};

const PerfumeForm = ({ initial, editing, onSave, onCancel, onUpload }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNote = (k, v) => setForm((f) => ({ ...f, notes: { ...f.notes, [k]: v } }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd);
      set("image_url", `${API}/files/${data.path}`);
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({ ...form, price: Number(form.price) || 0, position: Number(form.position) || 0 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="perfume-form" onSubmit={save} className="rounded-2xl border border-espresso/10 bg-cream p-6 sm:p-8">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-2xl text-espresso">{editing === "new" ? "Nouveau parfum" : "Modifier le parfum"}</h3>
        <button type="button" onClick={onCancel} data-testid="perfume-form-cancel" aria-label="Annuler" className="flex h-9 w-9 items-center justify-center rounded-full border border-espresso/15 hover:bg-ivory">
          <X size={15} />
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className={labelCls}>
          Nom du parfum
          <input data-testid="perfume-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Ex. Éclat de Soie" />
        </label>
        <div className="sm:col-span-2">
          <span className={labelCls}>Collections — plusieurs possibles</span>
          <div className="mt-2.5 flex flex-wrap gap-x-6 gap-y-2">
            {COLLECTIONS.map((c) => (
              <label key={c.slug} className="flex cursor-pointer items-center gap-2 text-sm text-espresso/80">
                <input
                  data-testid={`perfume-collection-${c.slug}`}
                  type="checkbox"
                  checked={form.collections.includes(c.slug)}
                  onChange={(e) =>
                    set("collections", e.target.checked ? [...form.collections, c.slug] : form.collections.filter((s) => s !== c.slug))
                  }
                  className="h-4 w-4 accent-[#C0A062]"
                />
                {c.name}
              </label>
            ))}
          </div>
        </div>
        <label className={labelCls}>
          Prix (€)
          <input data-testid="perfume-price-input" required type="number" step="0.01" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} placeholder="29.90" />
        </label>
        <label className={labelCls}>
          Contenance
          <input data-testid="perfume-size-input" value={form.size} onChange={(e) => set("size", e.target.value)} className={inputCls} placeholder="100 ml" />
        </label>
        <label className={`${labelCls} sm:col-span-2`}>
          Accroche courte
          <input data-testid="perfume-short-desc-input" value={form.short_desc} onChange={(e) => set("short_desc", e.target.value)} className={inputCls} placeholder="Floral poudré — rose, iris, musc." />
        </label>
        <label className={`${labelCls} sm:col-span-2`}>
          Description
          <textarea data-testid="perfume-description-input" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} placeholder="Une description élégante de votre parfum…" />
        </label>
        <label className={`${labelCls} sm:col-span-2`}>
          Conseils d'utilisation (pulvérisation, tenue)
          <textarea data-testid="perfume-conseils-input" rows={3} value={form.conseils} onChange={(e) => set("conseils", e.target.value)} className={inputCls} placeholder="Sur peau sèche, vaporisez à 15 cm sur les points chauds : nuque, poignets… Deux pressions suffisent…" />
        </label>
        <label className={labelCls}>
          Notes de tête
          <input data-testid="perfume-note-tete-input" value={form.notes.tete} onChange={(e) => setNote("tete", e.target.value)} className={inputCls} placeholder="Bergamote, poivre rose" />
        </label>
        <label className={labelCls}>
          Notes de cœur
          <input data-testid="perfume-note-coeur-input" value={form.notes.coeur} onChange={(e) => setNote("coeur", e.target.value)} className={inputCls} placeholder="Rose, iris" />
        </label>
        <label className={`${labelCls} sm:col-span-2`}>
          Notes de fond
          <input data-testid="perfume-note-fond-input" value={form.notes.fond} onChange={(e) => setNote("fond", e.target.value)} className={inputCls} placeholder="Musc, vanille" />
        </label>
        <label className={labelCls}>
          Position (ordre d'affichage)
          <input data-testid="perfume-position-input" type="number" min="0" value={form.position} onChange={(e) => set("position", e.target.value)} className={inputCls} />
        </label>
        <div className="flex items-end gap-6 pb-1">
          <label className="flex items-center gap-2 text-sm text-espresso/80">
            <input data-testid="perfume-featured-input" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[#C0A062]" />
            Coup de cœur
          </label>
          <label className="flex items-center gap-2 text-sm text-espresso/80">
            <input data-testid="perfume-visible-input" type="checkbox" checked={form.visible} onChange={(e) => set("visible", e.target.checked)} className="h-4 w-4 accent-[#C0A062]" />
            Visible
          </label>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-5">
        {form.image_url ? (
          <img src={form.image_url} alt="Aperçu" className="h-28 w-24 rounded-lg object-cover" data-testid="perfume-image-preview" />
        ) : (
          <div className="flex h-28 w-24 items-center justify-center rounded-lg bg-sand font-serif text-3xl text-taupe/50">V</div>
        )}
        <input ref={fileRef} data-testid="perfume-image-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
        <div className="flex flex-col gap-2">
          <button
            type="button"
            data-testid="perfume-image-upload-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-pill border border-espresso/25 px-6 py-3 text-espresso hover:border-espresso disabled:opacity-50"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? "Téléversement…" : "Téléverser une photo"}
          </button>
          <p className="text-[11px] text-espresso/50">JPG, PNG ou WebP — 8 Mo max.</p>
        </div>
      </div>

      <button data-testid="perfume-save-button" disabled={busy} className="btn-pill mt-8 w-full bg-espresso py-4 text-ivory hover:bg-noir disabled:opacity-60 sm:w-auto sm:px-12">
        {busy ? <Loader2 size={14} className="animate-spin" /> : "Enregistrer"}
      </button>
    </form>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [perfumes, setPerfumes] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_number: "", referral_link: "", contact_email: "" });
  const [tab, setTab] = useState("catalogue");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const load = async () => {
    try {
      const [p, s, o] = await Promise.all([api.get("/admin/perfumes"), api.get("/settings"), api.get("/admin/orders")]);
      setPerfumes(p.data);
      setSettings(s.data);
      setOrders(Array.isArray(o.data) ? o.data : []);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-espresso">
        <Loader2 className="animate-spin text-champagne" size={28} />
      </div>
    );
  }
  if (!user) return <Login onLogin={setUser} />;

  const logout = async () => {
    await api.post("/admin/logout").catch(() => {});
    setUser(null);
  };

  const savePerfume = async (payload) => {
    try {
      if (editing === "new") await api.post("/admin/perfumes", payload);
      else await api.put(`/admin/perfumes/${editing}`, payload);
      toast.success(editing === "new" ? "Parfum ajouté au catalogue" : "Parfum mis à jour");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » du catalogue ?`)) return;
    try {
      await api.delete(`/admin/perfumes/${p.id}`);
      toast.success("Parfum supprimé");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Réglages enregistrés");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const setOrderStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Supprimer la commande de ${o.name} ?`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      setOrders((os) => os.filter((x) => x.id !== o.id));
      toast.success("Commande supprimée");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const colName = (slug) => COLLECTIONS.find((c) => c.slug === slug)?.name || slug;

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-50 border-b border-espresso/10 bg-ivory/90 backdrop-blur">
        <div className="wrap flex h-16 items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-2xl text-espresso">Vāsam</span>
            <span className="hidden text-[9px] uppercase tracking-[0.35em] text-champagne sm:block">Espace consultante</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="hidden text-[10px] uppercase tracking-[0.25em] text-espresso/60 hover:text-espresso sm:block">
              Voir la boutique
            </Link>
            <button data-testid="admin-logout-button" onClick={logout} className="btn-pill border border-espresso/25 px-5 py-2.5 text-espresso hover:border-espresso">
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="wrap py-10 sm:py-14">
        <div className="flex gap-3">
          {[
            ["catalogue", "Catalogue"],
            ["reglages", "Réglages"],
            ["commandes", orders.filter((o) => (o.status || "nouvelle") === "nouvelle").length ? `Commandes (${orders.filter((o) => (o.status || "nouvelle") === "nouvelle").length})` : "Commandes"],
          ].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => {
                setTab(id);
                setEditing(null);
              }}
              className={`rounded-full border px-6 py-2.5 text-[10px] uppercase tracking-[0.22em] transition-all ${
                tab === id ? "border-espresso bg-espresso text-ivory" : "border-espresso/20 text-espresso/70 hover:border-espresso/60"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "catalogue" && (
          <div className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-serif text-3xl text-espresso">
                Vos parfums <span className="text-base text-taupe">({perfumes.length})</span>
              </h2>
              {editing === null && (
                <button
                  data-testid="new-perfume-button"
                  onClick={() => setEditing("new")}
                  className="btn-pill bg-espresso px-7 py-3.5 text-ivory hover:bg-noir"
                >
                  <Plus size={14} /> Nouveau parfum
                </button>
              )}
            </div>

            {editing !== null && (
              <div className="mt-8">
                <PerfumeForm
                  initial={editing === "new" ? emptyForm : perfumes.find((p) => p.id === editing) || emptyForm}
                  editing={editing}
                  onSave={savePerfume}
                  onCancel={() => setEditing(null)}
                />
              </div>
            )}

            <div className="mt-8 space-y-4">
              {perfumes.map((p) => (
                <div key={p.id} data-testid={`admin-perfume-row-${p.slug}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-espresso/10 bg-cream p-4 sm:flex-nowrap">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="h-20 w-16 shrink-0 rounded-lg object-cover" />
                  ) : (
                    <div className="flex h-20 w-16 shrink-0 items-center justify-center rounded-lg bg-sand font-serif text-2xl text-taupe/50">V</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-xl leading-tight text-espresso">{p.name}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-taupe">
                      {(p.collections || []).map(colName).join(" · ")} · {p.size} · position {p.position}
                      {p.featured ? " · coup de cœur" : ""}
                      {!p.visible ? " · masqué" : ""}
                    </p>
                  </div>
                  <p className="font-serif text-lg text-espresso">{formatPrice(p.price)}</p>
                  <div className="flex gap-2">
                    <button
                      data-testid={`perfume-edit-${p.slug}`}
                      onClick={() => setEditing(p.id)}
                      aria-label={`Modifier ${p.name}`}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-espresso/15 text-espresso transition-colors hover:bg-espresso hover:text-ivory"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`perfume-delete-${p.slug}`}
                      onClick={() => remove(p)}
                      aria-label={`Supprimer ${p.name}`}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-espresso/15 text-espresso/60 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {!perfumes.length && <p className="py-10 text-center font-serif text-2xl italic text-espresso/50">Le catalogue est vide.</p>}
            </div>
          </div>
        )}

        {tab === "reglages" && (
          <form data-testid="admin-settings-form" onSubmit={saveSettings} className="mt-10 max-w-xl rounded-2xl border border-espresso/10 bg-cream p-6 sm:p-8">
            <h2 className="font-serif text-3xl text-espresso">Réglages</h2>
            <p className="mt-2 text-sm text-espresso/60">
              Ces informations alimentent les boutons de commande de la boutique.
            </p>
            <label className={`${labelCls} mt-6`}>
              Numéro WhatsApp (format international, ex : 33651812175)
              <input data-testid="settings-whatsapp-input" value={settings.whatsapp_number || ""} onChange={(e) => setSettings((s) => ({ ...s, whatsapp_number: e.target.value }))} className={inputCls} placeholder="33651812175" />
            </label>
            <label className={`${labelCls} mt-4`}>
              Email de contact
              <input data-testid="settings-email-input" type="email" value={settings.contact_email || ""} onChange={(e) => setSettings((s) => ({ ...s, contact_email: e.target.value }))} className={inputCls} placeholder="contact@vasam.fr" />
            </label>
            <button data-testid="settings-save-button" disabled={busy} className="btn-pill mt-7 w-full bg-espresso py-4 text-ivory hover:bg-noir disabled:opacity-60 sm:w-auto sm:px-12">
              {busy ? <Loader2 size={14} className="animate-spin" /> : "Enregistrer"}
            </button>
          </form>
        )}

        {tab === "commandes" && (
          <div className="mt-10" data-testid="admin-orders-section">
            <h2 className="font-serif text-3xl text-espresso">
              Commandes <span className="text-base text-taupe">({orders.length})</span>
            </h2>
            <div className="mt-8 space-y-4">
              {orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-row-${o.id}`} className="flex flex-wrap items-center gap-4 rounded-xl border border-espresso/10 bg-cream p-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-xl leading-tight text-espresso">
                      {o.name} — <span className="text-champagne">{o.quantity} × {o.perfume_name}</span>
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-taupe">
                      {new Date(o.created_at).toLocaleDateString("fr-FR")} · {o.phone || "sans téléphone"} · {o.email || "sans email"} · {colName(o.collection)}
                    </p>
                    {o.message && <p className="mt-2 text-sm italic text-espresso/70">« {o.message} »</p>}
                  </div>
                  <p className="font-serif text-lg text-espresso">{formatPrice(o.price * o.quantity)}</p>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`order-status-select-${o.id}`}
                      value={o.status || "nouvelle"}
                      onChange={(e) => setOrderStatus(o.id, e.target.value)}
                      className="rounded-lg border border-espresso/20 bg-cream px-3 py-2 text-xs text-espresso outline-none focus:border-champagne"
                    >
                      <option value="nouvelle">Nouvelle</option>
                      <option value="traitée">Traitée</option>
                      <option value="livrée">Livrée</option>
                    </select>
                    <button data-testid={`order-delete-${o.id}`} onClick={() => removeOrder(o)} aria-label={`Supprimer la commande de ${o.name}`} className="flex h-10 w-10 items-center justify-center rounded-full border border-espresso/15 text-espresso/60 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {!orders.length && <p className="py-10 text-center font-serif text-2xl italic text-espresso/50">Aucune commande pour le moment.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );}
